//@flow
import * as React from 'react';
import {
    Text,
    Box,
    Stack,
    Columns,
    Column,
    Divider,
    Icon as NeoIcon,
    Button as NeoButton,
    Tooltip
} from '@locus-taxy/neo-ui';
import theme from './FpVisitMarkerPopup.scss';
import moment from 'moment-timezone';
import { Containers } from 'common/enums/PlanFilterEnum';
import { PlanStatusEnum } from 'common/enums/PlanEnums';
import {
    PermissionMatch,
    AccessDeniedMessage,
    RoleOperationEnum
} from 'common/modules/rbac';
import { type TFunction } from 'react-i18next';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import { type MasterOutlet } from 'common/models/internal/MasterOutlet';
import { Units } from 'common/enums/UnitEnum';

type Props = {
    onContextMenuOpen: (beat: string, visit: string) => void,
    beatId: string,
    visit: Object,
    index: number,
    salesCategory: string,
    salesmanId: string,
    outletData: MasterOutlet,
    getContainer: () => string,
    onPopUpCloseClick: () => void,
    status: string,
    t: TFunction,
    UnitConverter: UnitConversionHelper
};

export default class FpVisitMarkerPopup extends React.PureComponent<Props> {
    onContextMenuClick: (e: Event) => void = e => {
        e.stopPropagation();
        const {
            onContextMenuOpen,
            beatId,
            visit,
            isUnallocated,
            onContextMenuUnallocatedVisits
        } = this.props;
        if (isUnallocated) {
            onContextMenuUnallocatedVisits(e.clientX, e.clientY, visit.id);
        } else {
            onContextMenuOpen(
                e.clientX,
                e.clientY,
                beatId,
                visit.outletVisitId
            );
        }
    };

    renderOverflowButton: (
        hasPermission: boolean
    ) => React.Node = hasPermissions => {
        const { t } = this.props;
        return (
            <Tooltip
                shouldWrapChildren
                tooltip={
                    !hasPermissions ? (
                        <AccessDeniedMessage
                            action={t('entityManagement:fpPlanView.planEdits')}
                        />
                    ) : null
                }
            >
                <NeoButton
                    disabled={!hasPermissions}
                    appearance="icon"
                    icon="overflow_vertical"
                    onClick={this.onContextMenuClick}
                />
            </Tooltip>
        );
    };

    render() {
        const {
            salesCategory,
            salesmanId,
            outletData,
            visit,
            getContainer,
            onPopUpCloseClick,
            status,
            isUnallocated,
            UnitConverter,
            t
        } = this.props;
        const address = outletData.address.locationAddress;
        const { name: customerName, planFrequency } = outletData;
        const { id: locationId } = outletData.address;
        const {
            placeName,
            formattedAddress,
            locality,
            city,
            state,
            countryCode,
            pincode
        } = address;
        const addressString = [
            placeName,
            formattedAddress,
            locality,
            city,
            state,
            countryCode,
            pincode
        ]
            .filter(s => s)
            .join(', ');
        let startTimeLabel;
        let endTimeLabel;
        let transactionDuration;
        let breaches;
        const { volume } = visit;
        if (getContainer() === Containers.Allocated && !isUnallocated) {
            const { startTime, completedTime } = visit.eta;
            ({ breaches } = visit);
            startTimeLabel = moment(startTime).format('h:mm a');
            endTimeLabel = moment(completedTime).format('h:mm a');
            transactionDuration = visit.visitTat || 15;
        } else {
            const {
                start: startTime,
                end: completedTime
            } = visit.address.timeWindow.slot;
            startTimeLabel = moment(startTime).format('h:mm a');
            endTimeLabel = moment(completedTime).format('h:mm a');
            ({ transactionDuration } = visit.address.timeWindow);
        }
        let formattedTransactionDuration;
        if (transactionDuration > 60 * 60 - 1) {
            // greater then 59 mins
            formattedTransactionDuration = UnitConverter.convertSeconds({
                roundOff: true,
                value: transactionDuration
            }).valueString;
        } else {
            formattedTransactionDuration = UnitConverter.convertSeconds({
                roundOff: true,
                value: transactionDuration,
                conversionScale: Units.conversionScale.Base
            }).valueString;
        }
        return (
            <Box className={theme.popupInner} p={2}>
                <Stack space="xSmall">
                    <Box height="auto">
                        <Columns space="medium">
                            <Column width="content" height="inherit">
                                <NeoIcon
                                    color="grey.500"
                                    icon="dispatcher_pickup"
                                    size="large"
                                />
                            </Column>
                            <Column width="fluid">
                                <Stack space="small">
                                    {customerName ? (
                                        <Box>
                                            <Text fontSize={2} fontWeight={600}>
                                                {customerName}
                                            </Text>
                                        </Box>
                                    ) : null}
                                    <Box>
                                        <Text fontSize={1} fontWeight={500}>
                                            {outletData.sourceOutletId}
                                        </Text>
                                    </Box>
                                </Stack>
                            </Column>

                            <Column width="content">
                                <Columns space="small">
                                    <NeoButton
                                        appearance="icon"
                                        icon="cross"
                                        onClick={onPopUpCloseClick}
                                    />
                                    {status !== PlanStatusEnum.Frozen ? (
                                        <PermissionMatch
                                            operations={
                                                RoleOperationEnum.EDIT_PJP
                                            }
                                            render={this.renderOverflowButton}
                                        />
                                    ) : null}
                                </Columns>
                            </Column>
                        </Columns>
                    </Box>
                    <Box height="auto">
                        <Columns space="medium">
                            <Column width="content">
                                <NeoIcon
                                    color="grey.500"
                                    icon="location_on"
                                    size="large"
                                />
                            </Column>
                            <Column>
                                <Text>{addressString}</Text>
                            </Column>
                        </Columns>
                    </Box>
                    <Divider color="grey.200" />
                    <Box p={1}>
                        <Columns space="small">
                            <Text
                                fontWeight={500}
                                fontSize={1}
                                color="grey.500"
                            >
                                {t('entityManagement:fpPlanView.locationId')}
                            </Text>
                            <Text> {locationId}</Text>
                        </Columns>
                    </Box>
                    <Box p={1}>
                        <Columns space="small">
                            <Text
                                fontWeight={500}
                                fontSize={1}
                                color="grey.500"
                            >
                                {t('entityManagement:fpPlanView.salesCategory')}
                            </Text>
                            <Text> {salesCategory}</Text>
                        </Columns>
                    </Box>
                    <Box p={1}>
                        <Columns space="small">
                            <Text
                                fontWeight={500}
                                fontSize={1}
                                color="grey.500"
                            >
                                {t('entityManagement:fpPlanView.salesmanId')}
                            </Text>
                            <Text> {salesmanId}</Text>
                        </Columns>
                    </Box>
                    <Box p={1}>
                        <Columns space="small">
                            <Text
                                fontWeight={500}
                                fontSize={1}
                                color="grey.500"
                            >
                                {t(
                                    'entityManagement:masterOutletProperties.frequencyOfVisit'
                                )}
                            </Text>
                            <Text> {planFrequency}</Text>
                        </Columns>
                    </Box>
                    <Box p={1}>
                        <Columns space="small">
                            <Text
                                fontWeight={500}
                                fontSize={1}
                                color="grey.500"
                            >
                                {t(
                                    'entityManagement:masterOutletProperties.volume'
                                )}
                            </Text>
                            <Text> {volume}</Text>
                        </Columns>
                    </Box>

                    {breaches && breaches.length ? (
                        <React.Fragment>
                            <Divider color="grey.200" />
                            <Box p={1} height="auto">
                                <Columns space="medium">
                                    <Column width="content">
                                        <NeoIcon
                                            color="red.500"
                                            icon="circle"
                                            size="medium"
                                        />
                                    </Column>
                                    <Column width="fluid">
                                        {breaches.map((breach, index) => {
                                            return (
                                                <React.Fragment
                                                    key={`${index}`}
                                                >
                                                    <Text>
                                                        {t(
                                                            'entityManagement:fpPlanView.breachDesc',
                                                            {
                                                                count: Math.round(
                                                                    breach.breachValue /
                                                                        60
                                                                )
                                                            }
                                                        )}
                                                    </Text>
                                                </React.Fragment>
                                            );
                                        })}
                                    </Column>
                                </Columns>
                            </Box>
                        </React.Fragment>
                    ) : null}

                    <Divider color="grey.200" />
                    <Box p={1} height="auto">
                        <Columns>
                            <Column>
                                <Columns space="small">
                                    <NeoIcon
                                        color="grey.500"
                                        icon="location_on"
                                        size="medium"
                                    />
                                    <Text>{startTimeLabel}</Text>
                                </Columns>
                            </Column>

                            <Column>
                                <Columns>
                                    <NeoIcon
                                        color="grey.500"
                                        icon="dispatcher_eta"
                                        size="medium"
                                    />
                                    <Text>{endTimeLabel}</Text>
                                </Columns>
                            </Column>
                            <Column>
                                <Columns space="small">
                                    <NeoIcon
                                        color="grey.500"
                                        icon="clock"
                                        size="medium"
                                    />
                                    <Text>{formattedTransactionDuration}</Text>
                                </Columns>
                            </Column>
                        </Columns>
                    </Box>
                </Stack>
            </Box>
        );
    }
}
